import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";

/**
 * Route guard for super-admin-only pages (``/onboard``, ``/impersonate``,
 * ``/platform/catalog``). Uses the acting-as profile so an impersonated
 * partner_admin is correctly redirected back to ``/dashboard`` instead
 * of landing on a page their target role can't use.
 *
 * Defense-in-depth: the backend is still the real authority - every
 * route's handlers enforce role / scope independently - but hiding the
 * rendered page keeps the UX clean (no flash of a form that would 403
 * on submit).
 *
 * Profile loading is handled one level up in ``PartnerScopeProvider``,
 * which blocks render until the cache is populated. So by the time this
 * guard runs, ``useRoleProfile()`` always resolves synchronously.
 */
export function RequireSuperAdmin(props: { children: ReactNode }): JSX.Element {
  const profile = useRoleProfile();
  if (!isSuperAdmin(profile)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{props.children}</>;
}
